/**
 * Create/Edit Invoice Component
 * Two-column layout with form (left) and live preview (right).
 * Supports adding line items from product catalog or manual entry.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Trash2, Save, Send, RefreshCw,
  Search, Package, AlertTriangle, CheckCircle, ShoppingCart, X,
  Building2, Briefcase, FileText, Bold, List, Indent, Heading, BookOpen
} from 'lucide-react';
import PriceBookItemPicker, { PriceBookPick } from './PriceBookItemPicker';
import {
  invoiceAPI,
  Invoice,
  InvoiceLineItem,
  CreateInvoiceRequest,
  LineItemRequest,
  formatCents,
  decimalToCents,
  centsToDecimal,
} from '@/services/api/invoiceAPI';
import { salesPipelineAPI, Product as PipelineProduct, Opportunity } from '@/services/api/salesPipelineAPI';
import { organizationsAPI, Organization } from '@/services/api/organizations';
import { useRevOps, InvoiceTemplate } from '@/contexts/RevOpsContext';
import { InvoicePreview } from './InvoicePreview';

interface CreateEditInvoiceProps {
  organizationId: number;
  organizationName: string;
  invoiceId?: number | null;
  duplicateFromId?: number | null;
  onBack: () => void;
  onCreated: (invoice: Invoice) => void;
}

interface LineItemForm {
  tempId: string;
  id?: number;
  line_type: string;
  product_id?: string;
  product_code?: string;
  item_name: string;
  description: string;
  quantity: number;
  unit_price: number; // Dollars, not cents
  taxable: boolean;
  discount_type?: string;
  discount_value: number;
  total_contract_amount: number;
  total_installments: number;
  installment_number: number;
}

function generateTempId(): string {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function CreateEditInvoice({
  organizationId,
  organizationName,
  invoiceId,
  duplicateFromId,
  onBack,
  onCreated,
}: CreateEditInvoiceProps) {
  const isEditing = !!invoiceId;

  // Form state
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCompany, setCustomerCompany] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerState, setCustomerState] = useState('');
  const [customerZip, setCustomerZip] = useState('');
  // Use local date (not UTC) to avoid off-by-one timezone issues
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [dueDate, setDueDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [taxRate, setTaxRate] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [memo, setMemo] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [lineItems, setLineItems] = useState<LineItemForm[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Product catalog state
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showPriceBook, setShowPriceBook] = useState(false);
  const [products, setProducts] = useState<PipelineProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Customer lookup state
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  // Won deals state
  const [showWonDealPicker, setShowWonDealPicker] = useState(false);
  const [wonDeals, setWonDeals] = useState<Opportunity[]>([]);
  const [wonDealsLoading, setWonDealsLoading] = useState(false);
  const [dealSearch, setDealSearch] = useState('');
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);

  // Template state — auto-select default template
  const { invoiceTemplates } = useRevOps();
  const defaultTemplate = invoiceTemplates.find(t => t.isDefault) || invoiceTemplates[0] || null;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(defaultTemplate?.id || null);
  const selectedTemplate = selectedTemplateId
    ? invoiceTemplates.find(t => t.id === selectedTemplateId)
    : null;

  // Auto-select default template if templates load after mount or current selection is stale
  useEffect(() => {
    if (defaultTemplate) {
      const selectedExists = selectedTemplateId && invoiceTemplates.some(t => t.id === selectedTemplateId);
      if (!selectedExists) {
        setSelectedTemplateId(defaultTemplate.id);
      }
    }
  }, [defaultTemplate?.id, invoiceTemplates.length]);

  // Recalculate due date when payment terms or invoice date changes
  useEffect(() => {
    if (!invoiceDate) return;
    if (isEditing && loading) return; // Don't override while loading existing invoice
    const term = paymentTerms.toLowerCase();
    let days = 30;
    if (term.includes('receipt') || term.includes('immediate')) {
      days = 0;
    } else {
      days = parseInt(paymentTerms.replace(/\D/g, '')) || 30;
    }
    const due = new Date(invoiceDate);
    due.setDate(due.getDate() + days);
    setDueDate(due.toISOString().split('T')[0]);
  }, [invoiceDate, paymentTerms]);

  // Load existing invoice if editing
  useEffect(() => {
    if (isEditing && invoiceId) {
      setLoading(true);
      invoiceAPI.getInvoice(organizationId, invoiceId)
        .then(inv => {
          const cs = inv.customer_snapshot || {};
          setCustomerName(cs.name || '');
          setCustomerEmail(cs.email || '');
          setCustomerCompany(cs.company || '');
          setCustomerAddress(cs.address_line1 || '');
          setCustomerCity(cs.city || '');
          setCustomerState(cs.state || '');
          setCustomerZip(cs.postal_code || '');
          // Preserve the saved invoice date for all statuses (including drafts)
          setInvoiceDate(inv.invoice_date || '');
          setDueDate(inv.due_date || '');
          setPaymentTerms(inv.payment_terms || 'Net 30');
          setTaxRate(inv.tax_rate || 0);
          setDiscountPercent(inv.discount_percent || 0);
          setMemo(inv.memo || '');
          setNotes(inv.notes || '');
          setPaymentInstructions(inv.payment_instructions || '');

          if (inv.line_items) {
            setLineItems(inv.line_items.map(li => {
              // Split "ItemName - Description" for backward compat when metadata.item_name is missing
              let itemName = li.metadata?.item_name || '';
              let desc = li.description;
              if (!itemName && desc.includes(' - ')) {
                const dashIdx = desc.indexOf(' - ');
                itemName = desc.substring(0, dashIdx);
                desc = desc.substring(dashIdx + 3);
              }
              return {
                tempId: generateTempId(),
                id: li.id,
                line_type: li.line_type,
                product_id: li.product_id,
                product_code: li.product_code,
                item_name: itemName,
                description: desc,
                quantity: li.quantity,
                unit_price: centsToDecimal(li.unit_price_cents),
                taxable: li.taxable,
                discount_type: li.discount_type,
                discount_value: li.discount_value,
                total_contract_amount: li.metadata?.total_contract_amount || 0,
                total_installments: li.metadata?.total_installments || 1,
                installment_number: li.metadata?.installment_number || 1,
              };
            }));
          }
        })
        .catch(err => setError('Failed to load invoice'))
        .finally(() => setLoading(false));
    }
  }, [invoiceId, organizationId, isEditing]);

  // Load source invoice data when duplicating
  useEffect(() => {
    if (!isEditing && duplicateFromId) {
      setLoading(true);
      invoiceAPI.getInvoice(organizationId, duplicateFromId)
        .then(inv => {
          const cs = inv.customer_snapshot || {};
          setCustomerName(cs.name || '');
          setCustomerEmail(cs.email || '');
          setCustomerCompany(cs.company || '');
          setCustomerAddress(cs.address_line1 || '');
          setCustomerCity(cs.city || '');
          setCustomerState(cs.state || '');
          setCustomerZip(cs.postal_code || '');
          // Keep today's date for the duplicate (new invoice)
          setPaymentTerms(inv.payment_terms || 'Net 30');
          setTaxRate(inv.tax_rate || 0);
          setDiscountPercent(inv.discount_percent || 0);
          setMemo(inv.memo || '');
          setNotes(inv.notes || '');
          setPaymentInstructions(inv.payment_instructions || '');

          if (inv.line_items) {
            setLineItems(inv.line_items.map(li => {
              let itemName = li.metadata?.item_name || '';
              let desc = li.description;
              if (!itemName && desc.includes(' - ')) {
                const dashIdx = desc.indexOf(' - ');
                itemName = desc.substring(0, dashIdx);
                desc = desc.substring(dashIdx + 3);
              }
              return {
                tempId: generateTempId(),
                // Do not copy li.id — these are new line items
                line_type: li.line_type,
                product_id: li.product_id,
                product_code: li.product_code,
                item_name: itemName,
                description: desc,
                quantity: li.quantity,
                unit_price: centsToDecimal(li.unit_price_cents),
                taxable: li.taxable,
                discount_type: li.discount_type,
                discount_value: li.discount_value,
                total_contract_amount: li.metadata?.total_contract_amount || 0,
                total_installments: li.metadata?.total_installments || 1,
                installment_number: li.metadata?.installment_number || 1,
              };
            }));
          }
        })
        .catch(err => setError('Failed to load source invoice for duplication'))
        .finally(() => setLoading(false));
    }
  }, [duplicateFromId, organizationId, isEditing]);

  // Line item management
  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      tempId: generateTempId(),
      line_type: 'product',
      item_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      taxable: true,
      discount_value: 0,
      total_contract_amount: 0,
      total_installments: 1,
      installment_number: 1,
    }]);
  };

  const updateLineItem = (tempId: string, updates: Partial<LineItemForm>) => {
    setLineItems(prev => prev.map(li =>
      li.tempId === tempId ? { ...li, ...updates } : li
    ));
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(prev => prev.filter(li => li.tempId !== tempId));
  };

  // Fetch products from pipeline catalog
  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const prods = await salesPipelineAPI.products.getProducts({ isActive: true });
      setProducts(prods);
    } catch (err) {
      console.warn('Failed to fetch products:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  const openProductPicker = () => {
    setShowProductPicker(true);
    if (products.length === 0) {
      fetchProducts();
    }
  };

  const addPriceBookItem = (item: PriceBookPick) => {
    setLineItems(prev => [...prev, {
      tempId: generateTempId(),
      line_type: item.line_type,
      product_code: item.code,
      item_name: item.name,
      description: item.description,
      quantity: 1,
      unit_price: item.unit_price,
      taxable: false,
      discount_value: 0,
      total_contract_amount: 0,
      total_installments: 1,
      installment_number: 1,
    }]);
    setShowPriceBook(false);
  };

  const addProductAsLineItem = (product: PipelineProduct) => {
    setLineItems(prev => [...prev, {
      tempId: generateTempId(),
      line_type: product.billingType === 'recurring' ? 'subscription' : product.billingType === 'usage' ? 'usage' : 'product',
      product_id: product.id,
      product_code: product.sku,
      item_name: product.name,
      description: product.description || '',
      quantity: 1,
      unit_price: product.unitPrice,
      taxable: true,
      discount_value: 0,
      total_contract_amount: 0,
      total_installments: 1,
      installment_number: 1,
    }]);
    setShowProductPicker(false);
  };

  const filteredProducts = products.filter(p =>
    !productSearch ||
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.sku || '').toLowerCase().includes(productSearch.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  // Organization lookup
  const fetchOrganizations = async () => {
    setOrgsLoading(true);
    try {
      const organizations = await organizationsAPI.listOrganizations();
      setOrgs(organizations);
    } catch (err) {
      console.warn('Failed to fetch organizations:', err);
    } finally {
      setOrgsLoading(false);
    }
  };

  const openCustomerPicker = () => {
    setShowCustomerPicker(true);
    if (orgs.length === 0) fetchOrganizations();
  };

  const selectOrganization = (org: Organization) => {
    setCustomerName(org.billing_contact_name || org.name);
    setCustomerEmail(org.billing_contact_email || org.contact_email || '');
    setCustomerCompany(org.name);
    setCustomerAddress(org.billing_address_line1 || org.address || '');
    setCustomerCity(org.billing_city || '');
    setCustomerState(org.billing_state || '');
    setCustomerZip(org.billing_zip || '');
    setShowCustomerPicker(false);
  };

  const filteredOrgs = orgs.filter(o =>
    !customerSearch ||
    o.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (o.contact_email || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
    (o.billing_city || '').toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Won deal import
  const fetchWonDeals = async () => {
    setWonDealsLoading(true);
    try {
      const deals = await salesPipelineAPI.opportunities.getWonOpportunities();
      setWonDeals(deals);
    } catch (err) {
      console.warn('Failed to fetch won deals:', err);
    } finally {
      setWonDealsLoading(false);
    }
  };

  const openWonDealPicker = () => {
    setShowWonDealPicker(true);
    if (wonDeals.length === 0) fetchWonDeals();
    if (orgs.length === 0) fetchOrganizations();
  };

  const importFromDeal = (deal: Opportunity) => {
    setCustomerName(deal.company);
    setCustomerCompany(deal.company);
    setSelectedOpportunityId(deal.id);

    if (deal.companyId) {
      const org = orgs.find(o => o.id === deal.companyId);
      if (org) selectOrganization(org);
    }

    if (deal.lineItems && deal.lineItems.length > 0) {
      const newLineItems: LineItemForm[] = deal.lineItems.map(item => ({
        tempId: generateTempId(),
        line_type: item.billingType === 'recurring' ? 'subscription' :
                   item.billingType === 'usage' ? 'usage' :
                   item.billingType === 'transaction' ? 'service' : 'product',
        product_id: item.productId,
        item_name: item.itemName,
        description: item.description || '',
        quantity: item.quantity,
        unit_price: item.unitPrice,
        taxable: true,
        discount_type: item.discountType,
        discount_value: item.discountValue || 0,
        total_contract_amount: 0,
        total_installments: 1,
        installment_number: 1,
      }));
      setLineItems(newLineItems);
    }

    setShowWonDealPicker(false);
  };

  const filteredDeals = wonDeals.filter(d =>
    !dealSearch ||
    d.name.toLowerCase().includes(dealSearch.toLowerCase()) ||
    d.company.toLowerCase().includes(dealSearch.toLowerCase())
  );

  // Apply template defaults on selection (new invoices only)
  useEffect(() => {
    const tmpl = selectedTemplateId
      ? invoiceTemplates.find(t => t.id === selectedTemplateId)
      : null;
    if (tmpl && !isEditing) {
      if (tmpl.paymentInstructions) setPaymentInstructions(tmpl.paymentInstructions);
      if (tmpl.defaultDueDays) {
        setPaymentTerms(`Net ${tmpl.defaultDueDays}`);
        const due = new Date(invoiceDate);
        due.setDate(due.getDate() + tmpl.defaultDueDays);
        setDueDate(due.toISOString().split('T')[0]);
      }
      if (tmpl.footerText) setMemo(tmpl.footerText);
      if (tmpl.defaultNotes) setNotes(tmpl.defaultNotes);
    }
  }, [selectedTemplateId]);

  // Calculate totals
  const calculateLineAmount = (li: LineItemForm): number => {
    let amount = li.quantity * li.unit_price;
    if (li.discount_type === 'percent' && li.discount_value > 0) {
      amount -= amount * li.discount_value / 100;
    } else if (li.discount_type === 'fixed' && li.discount_value > 0) {
      amount -= li.discount_value;
    }
    return Math.max(amount, 0);
  };

  const subtotal = lineItems.reduce((sum, li) => sum + calculateLineAmount(li), 0);
  const discountAmount = subtotal * discountPercent / 100;
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * taxRate / 100;
  const total = subtotal - discountAmount + taxAmount;

  // Build preview data
  const previewData = {
    invoice_number: isEditing ? `Draft #${invoiceId}` : 'NEW',
    status: 'draft' as const,
    customer_name: customerName || 'Customer Name',
    customer_company: customerCompany,
    customer_email: customerEmail,
    customer_address: [customerAddress, [customerCity, customerState, customerZip].filter(Boolean).join(', ')].filter(Boolean).join('\n'),
    invoice_date: invoiceDate,
    due_date: dueDate,
    payment_terms: paymentTerms,
    line_items: lineItems.map(li => ({
      item_name: li.item_name || undefined,
      description: li.description || (li.item_name ? '' : 'Line item description'),
      quantity: li.quantity,
      unit_price: li.unit_price,
      amount: calculateLineAmount(li),
      installment_number: li.total_installments > 1 ? li.installment_number : undefined,
      total_installments: li.total_installments > 1 ? li.total_installments : undefined,
      total_contract_amount: li.total_installments > 1 ? li.total_contract_amount : undefined,
    })),
    subtotal,
    discount_amount: discountAmount,
    tax_amount: taxAmount,
    total,
    memo,
    notes,
    payment_instructions: paymentInstructions,
    organization_name: organizationName,
    show_portal_link: (selectedTemplate || defaultTemplate)?.showPortalLink ?? true,
    bank_details: (selectedTemplate || defaultTemplate)?.showBankDetails ? {
      bank_name: (selectedTemplate || defaultTemplate)!.bankName,
      account_name: (selectedTemplate || defaultTemplate)!.bankAccountName,
      account_number: (selectedTemplate || defaultTemplate)!.bankAccountNumber,
      routing_number: (selectedTemplate || defaultTemplate)!.bankRoutingNumber,
      swift_code: (selectedTemplate || defaultTemplate)!.bankSwiftCode,
    } : undefined,
    accepted_payment_methods: (selectedTemplate || defaultTemplate)?.acceptedPaymentMethods,
    payment_method_instructions: (selectedTemplate || defaultTemplate)?.paymentMethodInstructions,
    branding: (selectedTemplate || defaultTemplate) ? {
      logo_url: (selectedTemplate || defaultTemplate)!.showLogo ? (selectedTemplate || defaultTemplate)!.logoUrl : undefined,
      primary_color: (selectedTemplate || defaultTemplate)!.primaryColor,
      company_name: (selectedTemplate || defaultTemplate)!.companyName,
    } : undefined,
  };

  // Save
  const handleSave = async (finalize = false) => {
    if (lineItems.length === 0) {
      setError('Add at least one line item');
      return;
    }

    if (!customerName && !customerEmail) {
      setError('Add customer name or email');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const customerSnapshot = {
        name: customerName,
        email: customerEmail,
        company: customerCompany,
        address_line1: customerAddress,
        city: customerCity,
        state: customerState,
        postal_code: customerZip,
      };

      const items: LineItemRequest[] = lineItems.map(li => ({
        line_type: li.line_type as any,
        product_id: li.product_id,
        product_code: li.product_code,
        description: li.description,
        quantity: li.quantity,
        unit_price_cents: decimalToCents(li.unit_price),
        amount_cents: decimalToCents(calculateLineAmount(li)),
        taxable: li.taxable,
        discount_type: li.discount_type as any,
        discount_value: li.discount_value,
        metadata: {
          ...(li.item_name ? { item_name: li.item_name } : {}),
          ...(li.total_installments > 1 ? {
            total_installments: li.total_installments,
            installment_number: li.installment_number,
            total_contract_amount: li.total_contract_amount,
          } : {}),
        },
      }));

      let invoice: Invoice;

      if (isEditing && invoiceId) {
        // Update header fields
        await invoiceAPI.updateInvoice(organizationId, invoiceId, {
          customer_snapshot: customerSnapshot,
          invoice_date: invoiceDate,
          due_date: dueDate,
          payment_terms: paymentTerms,
          tax_rate: taxRate,
          discount_percent: discountPercent,
          memo,
          notes,
          payment_instructions: paymentInstructions,
        });

        // Update line items: delete existing, then re-add with current metadata
        const existing = await invoiceAPI.getInvoice(organizationId, invoiceId);
        if (existing.line_items) {
          for (const li of existing.line_items) {
            if (li.id) await invoiceAPI.deleteLineItem(organizationId, invoiceId, li.id);
          }
        }
        for (const item of items) {
          await invoiceAPI.addLineItem(organizationId, invoiceId, item);
        }

        invoice = await invoiceAPI.getInvoice(organizationId, invoiceId);
      } else {
        // Create new
        invoice = await invoiceAPI.createInvoice(organizationId, {
          customer_snapshot: customerSnapshot,
          opportunity_id: selectedOpportunityId || undefined,
          invoice_date: invoiceDate,
          due_date: dueDate,
          payment_terms: paymentTerms,
          tax_rate: taxRate,
          discount_percent: discountPercent,
          currency: 'USD',
          memo,
          notes,
          payment_instructions: paymentInstructions,
          line_items: items,
        });
      }

      if (finalize) {
        invoice = await invoiceAPI.finalizeInvoice(organizationId, invoice.id);
      }

      setSuccess(finalize ? 'Invoice finalized!' : 'Invoice saved as draft');
      setTimeout(() => onCreated(invoice), 500);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to save invoice';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Loading invoice...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={openWonDealPicker}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20"
          >
            <Briefcase className="w-4 h-4" />
            Import Won Deal
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || lineItems.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            Finalize
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* Won Deal Picker */}
      {showWonDealPicker && (
        <div className="border border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wider">
              <Briefcase className="w-3.5 h-3.5 inline mr-1" />Import from Won Deal
            </h4>
            <button onClick={() => setShowWonDealPicker(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <input
            type="text" placeholder="Search deals by name or company..."
            value={dealSearch} onChange={e => setDealSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-2"
            autoFocus
          />
          {wonDealsLoading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-4 h-4 animate-spin text-green-500 mr-2" />
              <span className="text-xs text-gray-500">Loading won deals...</span>
            </div>
          ) : filteredDeals.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-4">
              {wonDeals.length === 0 ? 'No won deals found. Close deals in Sales Pipeline first.' : 'No deals match your search.'}
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-1">
              {filteredDeals.map(deal => (
                <button key={deal.id} onClick={() => importFromDeal(deal)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-between group">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{deal.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {deal.company}
                      {deal.lineItems && deal.lineItems.length > 0 && (
                        <span className="ml-2">{deal.lineItems.length} line item{deal.lineItems.length !== 1 ? 's' : ''}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="text-sm font-medium text-green-600">${deal.amount?.toLocaleString() || '0'}</p>
                    {deal.closeDate && <p className="text-[10px] text-gray-400">Closed {deal.closeDate}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Form */}
        <div className="space-y-6">
          {/* Template Selector */}
          {invoiceTemplates.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                <FileText className="w-4 h-4 inline mr-1.5 text-blue-500" />
                Invoice Template
              </h3>
              <select
                value={selectedTemplateId || ''}
                onChange={e => setSelectedTemplateId(e.target.value || null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">None (Manual)</option>
                {invoiceTemplates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.companyName}{t.isDefault ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
              {(selectedTemplate || defaultTemplate) && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: (selectedTemplate || defaultTemplate)!.primaryColor }} />
                  <span>{(selectedTemplate || defaultTemplate)!.companyName}</span>
                  {(selectedTemplate || defaultTemplate)!.defaultDueDays && (
                    <span>| Net {(selectedTemplate || defaultTemplate)!.defaultDueDays}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Customer</h3>
              <button onClick={openCustomerPicker}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <Building2 className="w-3.5 h-3.5" /> From Organizations
              </button>
            </div>

            {/* Organization Picker */}
            {showCustomerPicker && (
              <div className="mb-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                    <Building2 className="w-3.5 h-3.5 inline mr-1" />Select Organization
                  </h4>
                  <button onClick={() => setShowCustomerPicker(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text" placeholder="Search organizations..."
                  value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-2"
                  autoFocus
                />
                {orgsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-4 h-4 animate-spin text-blue-500 mr-2" />
                    <span className="text-xs text-gray-500">Loading organizations...</span>
                  </div>
                ) : filteredOrgs.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">
                    {orgs.length === 0 ? 'No organizations found.' : 'No organizations match your search.'}
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {filteredOrgs.map(org => (
                      <button key={org.id} onClick={() => selectOrganization(org)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-between group">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{org.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {org.contact_email || 'No email'}
                            {org.billing_city && <span className="ml-2">{org.billing_city}, {org.billing_state}</span>}
                          </p>
                        </div>
                        {org.customer_status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ml-2 ${
                            org.customer_status === 'CUSTOMER' ? 'bg-green-100 text-green-700' :
                            org.customer_status === 'PROSPECT' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{org.customer_status}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Full Name *</label>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="john@example.com" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Company</label>
                <input type="text" value={customerCompany} onChange={e => setCustomerCompany(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Acme Corp" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Address</label>
                <input type="text" value={customerAddress} onChange={e => setCustomerAddress(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="123 Main St" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">City</label>
                <input type="text" value={customerCity} onChange={e => setCustomerCity(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">State</label>
                  <input type="text" value={customerState} onChange={e => setCustomerState(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">ZIP</label>
                  <input type="text" value={customerZip} onChange={e => setCustomerZip(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Invoice Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Invoice Date</label>
                <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Terms</label>
                <select value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  <option>Net 15</option>
                  <option>Net 30</option>
                  <option>Net 45</option>
                  <option>Net 60</option>
                  <option>Due on Receipt</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Currency</label>
                <input type="text" value="USD" disabled
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tax Rate (%)</label>
                <input type="number" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  step="0.01" min="0" max="100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Discount (%)</label>
                <input type="number" value={discountPercent} onChange={e => setDiscountPercent(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  step="0.01" min="0" max="100" />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Line Items</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowPriceBook(v => !v)}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                  <BookOpen className="w-3.5 h-3.5" /> From Price Book
                </button>
                <button onClick={openProductPicker}
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium">
                  <ShoppingCart className="w-3.5 h-3.5" /> From Catalog
                </button>
                <button onClick={addLineItem} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                  <Plus className="w-3.5 h-3.5" /> Manual Item
                </button>
              </div>
            </div>

            {showPriceBook && (
              <PriceBookItemPicker onPick={addPriceBookItem} onClose={() => setShowPriceBook(false)} />
            )}

            {/* Product Catalog Picker */}
            {showProductPicker && (
              <div className="mb-4 border border-purple-200 dark:border-purple-800 rounded-lg bg-purple-50 dark:bg-purple-900/20 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                    <ShoppingCart className="w-3.5 h-3.5 inline mr-1" />Product Catalog
                  </h4>
                  <button onClick={() => setShowProductPicker(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text" placeholder="Search products by name, SKU, category..."
                  value={productSearch} onChange={e => setProductSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-2"
                  autoFocus
                />
                {productsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-4 h-4 animate-spin text-purple-500 mr-2" />
                    <span className="text-xs text-gray-500">Loading products...</span>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-4">
                    {products.length === 0 ? 'No products in catalog. Add products in Sales Pipeline first.' : 'No products match your search.'}
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {filteredProducts.map(p => (
                      <button key={p.id} onClick={() => addProductAsLineItem(p)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors flex items-center justify-between group">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {p.sku && <span className="mr-2">{p.sku}</span>}
                            {p.category && <span className="mr-2">{p.category}</span>}
                            {p.pricingModel && <span className="capitalize">{p.pricingModel.replace(/_/g, ' ')}</span>}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">${p.unitPrice.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-400 capitalize">{p.billingType?.replace(/_/g, ' ') || 'one time'}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {lineItems.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No line items yet</p>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <button onClick={openProductPicker} className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                    Add from Product Catalog
                  </button>
                  <span className="text-gray-300">or</span>
                  <button onClick={addLineItem} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                    Add manually
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {lineItems.map((li, idx) => (
                  <div key={li.tempId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-gray-400 mt-2 w-6 text-center">{idx + 1}</span>
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-5">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Item / Product *</label>
                            <input
                              type="text" placeholder="Product or service name"
                              value={li.item_name}
                              onChange={e => updateLineItem(li.tempId, { item_name: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                          <div className="col-span-6">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Description</label>
                            <div className="border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 overflow-hidden">
                              {/* Mini formatting toolbar */}
                              <div className="flex items-center gap-0.5 px-1 py-0.5 bg-gray-50 dark:bg-gray-600 border-b border-gray-200 dark:border-gray-500">
                                <button type="button" title="Bold (**text**)"
                                  onClick={() => {
                                    const ta = document.getElementById(`desc-${li.tempId}`) as HTMLTextAreaElement;
                                    if (!ta) return;
                                    const start = ta.selectionStart, end = ta.selectionEnd;
                                    const text = li.description;
                                    if (start !== end) {
                                      const updated = text.slice(0, start) + '**' + text.slice(start, end) + '**' + text.slice(end);
                                      updateLineItem(li.tempId, { description: updated });
                                    } else {
                                      const updated = text.slice(0, start) + '**bold**' + text.slice(start);
                                      updateLineItem(li.tempId, { description: updated });
                                    }
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300">
                                  <Bold className="w-3 h-3" />
                                </button>
                                <button type="button" title="Bullet point"
                                  onClick={() => {
                                    const ta = document.getElementById(`desc-${li.tempId}`) as HTMLTextAreaElement;
                                    if (!ta) return;
                                    const pos = ta.selectionStart;
                                    const text = li.description;
                                    // Find the start of current line
                                    const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
                                    const linePrefix = text.slice(lineStart, lineStart + 2);
                                    if (linePrefix === '- ') {
                                      // Remove bullet
                                      const updated = text.slice(0, lineStart) + text.slice(lineStart + 2);
                                      updateLineItem(li.tempId, { description: updated });
                                    } else {
                                      // Add bullet
                                      const updated = text.slice(0, lineStart) + '- ' + text.slice(lineStart);
                                      updateLineItem(li.tempId, { description: updated });
                                    }
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300">
                                  <List className="w-3 h-3" />
                                </button>
                                <button type="button" title="Indent"
                                  onClick={() => {
                                    const ta = document.getElementById(`desc-${li.tempId}`) as HTMLTextAreaElement;
                                    if (!ta) return;
                                    const pos = ta.selectionStart;
                                    const text = li.description;
                                    const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
                                    const updated = text.slice(0, lineStart) + '  ' + text.slice(lineStart);
                                    updateLineItem(li.tempId, { description: updated });
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300">
                                  <Indent className="w-3 h-3" />
                                </button>
                                <button type="button" title="Heading"
                                  onClick={() => {
                                    const text = li.description;
                                    const ta = document.getElementById(`desc-${li.tempId}`) as HTMLTextAreaElement;
                                    if (!ta) return;
                                    const pos = ta.selectionStart;
                                    const lineStart = text.lastIndexOf('\n', pos - 1) + 1;
                                    const linePrefix = text.slice(lineStart, lineStart + 2);
                                    if (linePrefix === '# ') {
                                      const updated = text.slice(0, lineStart) + text.slice(lineStart + 2);
                                      updateLineItem(li.tempId, { description: updated });
                                    } else {
                                      const updated = text.slice(0, lineStart) + '# ' + text.slice(lineStart);
                                      updateLineItem(li.tempId, { description: updated });
                                    }
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-600 dark:text-gray-300">
                                  <Heading className="w-3 h-3" />
                                </button>
                              </div>
                              <textarea
                                id={`desc-${li.tempId}`}
                                placeholder="Description with formatting (bold, bullets, headings)"
                                value={li.description}
                                onChange={e => updateLineItem(li.tempId, { description: e.target.value })}
                                rows={2}
                                className="w-full px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 bg-transparent border-0 focus:ring-0 focus:outline-none resize-y min-h-[36px]"
                              />
                            </div>
                          </div>
                          <div className="col-span-1 flex items-end">
                            <button onClick={() => removeLineItem(li.tempId)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-3">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Qty</label>
                            <input
                              type="number" value={li.quantity} min="0" step="0.01"
                              onChange={e => updateLineItem(li.tempId, { quantity: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Unit Price ($)</label>
                            <input
                              type="number" value={li.unit_price} min="0" step="0.01"
                              onChange={e => updateLineItem(li.tempId, { unit_price: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Amount</label>
                            <div className="px-2 py-1.5 text-sm bg-gray-50 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium">
                              ${calculateLineAmount(li).toFixed(2)}
                            </div>
                          </div>
                        </div>
                        {/* Installment Tracking */}
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-4">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Total Contract ($)</label>
                            <input
                              type="number" value={li.total_contract_amount || ''} min="0" step="0.01"
                              placeholder="0.00"
                              onChange={e => updateLineItem(li.tempId, { total_contract_amount: parseFloat(e.target.value) || 0 })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[10px] text-gray-400 mb-0.5">Installment # of</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number" value={li.installment_number} min="1" step="1"
                                onChange={e => updateLineItem(li.tempId, { installment_number: parseInt(e.target.value) || 1 })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                              <span className="text-xs text-gray-400">of</span>
                              <input
                                type="number" value={li.total_installments} min="1" step="1"
                                onChange={e => updateLineItem(li.tempId, { total_installments: parseInt(e.target.value) || 1 })}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              />
                            </div>
                          </div>
                          {li.total_installments > 1 && li.total_contract_amount > 0 && (
                            <div className="col-span-4 flex items-end">
                              <div className="text-xs text-gray-500 pb-1">
                                Remaining: <span className="font-medium text-gray-700 dark:text-gray-300">${(li.total_contract_amount - calculateLineAmount(li)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Totals */}
            {lineItems.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900 dark:text-gray-100">${subtotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Discount ({discountPercent}%)</span>
                    <span className="text-red-600">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Tax ({taxRate}%)</span>
                    <span className="text-gray-900 dark:text-gray-100">${taxAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-gray-900 dark:text-gray-100">Total</span>
                  <span className="text-gray-900 dark:text-gray-100">${total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Additional Information</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Memo (visible to customer)</label>
                <textarea value={memo} onChange={e => setMemo(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Thank you for your business!" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Internal Notes (not visible to customer)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Internal notes..." />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Instructions</label>
                <textarea value={paymentInstructions} onChange={e => setPaymentInstructions(e.target.value)} rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Please make checks payable to..." />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Live Preview */}
        <div className="lg:sticky lg:top-6 self-start">
          <InvoicePreview data={previewData} />
        </div>
      </div>
    </div>
  );
}
